// src/features/security/SecurityPage.tsx
import { useEffect, useState } from 'react';
import { Activity, ShieldAlert, ShieldCheck, UsersRound } from 'lucide-react';
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { SecurityHeader } from './components';
import { getSecurityOverview, getSecuritySessions } from './api/security.api';
import type { SecurityOverview, SecuritySession } from './types/security.types';
import './styles/security.css';

/**
 * Security Foundation. Same layout pattern as CustomersPage/
 * ServicesPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Every row here comes from features/security/data/security.mock.ts
 * via useSecurityMock() — no security endpoint exists yet.
 * useSecurityMock is the seam a later phase swaps for a real API hook
 * (same shape, `{ modules }`); SecurityTable only depends on the
 * `modules` prop, not on where it came from, so that swap won't touch
 * this file or SecurityTable.tsx.
 */
export function SecurityPage() {
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [sessions, setSessions] = useState<SecuritySession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const hasData = Boolean(overview) || sessions.length > 0;
  const initialLoading = loading && !hasData;

  useEffect(() => {
    Promise.all([getSecurityOverview(), getSecuritySessions()])
      .then(([nextOverview, nextSessions]) => { setOverview(nextOverview); setSessions(nextSessions); })
      .catch(() => setError('خطا در دریافت اطلاعات امنیتی'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ControlPlanePage
      className="vania-security-control-plane-page"
      title="مرکز امنیت"
      summary={initialLoading ? <SecurityLoadingSkeleton /> : overview ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'نشست‌های فعال', value: formatPersianNumber(overview.active_sessions), icon: <ShieldCheck size={16} />, tone: 'success' },
            { label: 'رخدادهای امنیتی', value: formatPersianNumber(overview.security_events), icon: <Activity size={16} />, tone: 'primary' },
            { label: 'نقش‌ها', value: formatPersianNumber(overview.roles_count), icon: <UsersRound size={16} />, tone: 'warning' },
            { label: 'حساب‌های پرریسک ورود', value: formatPersianNumber(overview.login_risk_accounts), icon: <ShieldAlert size={16} />, tone: 'danger' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          <SecurityHeader />
          {error && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          {!loading && !error && !hasData ? <EmptyState message="اطلاعات امنیتی برای نمایش وجود ندارد." /> : null}
          {!initialLoading && sessions.length > 0 ? (
            <div className="vania-security-table-region">
              <div className="vania-security-table-scroll">
                <table className="vania-security-table">
                  <thead><tr><th>نماینده</th><th>موبایل</th><th>وضعیت</th><th>ایجاد</th><th>انقضا</th></tr></thead>
                  <tbody>{sessions.map((session) => <tr key={session.id}><td>{session.full_name || session.representative_id}</td><td dir="ltr">{session.mobile}</td><td>{session.status}</td><td dir="ltr">{session.created_at}</td><td dir="ltr">{session.expires_at}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          ) : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function SecurityLoadingSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" className="vania-security-loading-system" label="در حال بارگذاری اطلاعات امنیتی">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4 vania-security-summary-skeleton" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={`security-metric-skeleton-${index}`} className="vania-security-summary-skeleton-card">
            <VaniaContentSkeleton style={{ width: '42%', height: '0.75rem' }} />
            <VaniaContentSkeleton style={{ width: '58%', height: '1.2rem' }} />
          </div>
        ))}
      </div>
      <div className="vania-security-table-scroll" aria-hidden="true">
        <table className="vania-security-table">
          <thead>
            <tr>
              {['نماینده', 'موبایل', 'وضعیت', 'ایجاد', 'انقضا'].map((heading) => <th key={heading}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, rowIndex) => (
              <tr key={`security-session-skeleton-${rowIndex}`}>
                {Array.from({ length: 5 }, (_, cellIndex) => (
                  <td key={`security-session-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </VaniaLoadingSystem>
  );
}
